import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, ShieldCheck, Clock, Send, Play, Pause, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import Seo from "../components/Seo";
import { useLang } from "../context/LanguageContext";
import { CODE_TO_URL } from "../lib/i18nRouter";
import { pickViseme } from "../components/GemologistAssistant";
import MouthCanvas from "../components/MouthCanvas";
import TalkingHeadVideo from "../components/TalkingHeadVideo";

/**
 * /:lang/consultation/success
 *
 * Lifecycle:
 *  1. Stripe redirects here with ?session_id=&c=consultation_id (or we read it from localStorage).
 *  2. Poll GET /api/consultation/status/{id} every 2s until payment_status=paid (max 30s).
 *  3. Show the Q&A textarea + master avatar.
 *  4. On submit → POST /api/consultation/ask → render the answer + auto-speak via Web Speech API.
 */
const PAYMENT_POLL_INTERVAL_MS = 2000;
const PAYMENT_POLL_TIMEOUT_MS = 30000;

const ConsultationSuccess = () => {
  const [search] = useSearchParams();
  const { lang, apiLang } = useLang();
  const slug = CODE_TO_URL[lang] || "en";
  const isDemo = search.get("demo") === "1";
  const consultationId = search.get("c")
    || (typeof window !== "undefined" ? localStorage.getItem("opik_consult_pending") : null);

  const [statusState, setStatusState] = useState(isDemo ? "paid" : "verifying"); // verifying | paid | failed | answered
  const [remaining, setRemaining] = useState(isDemo ? 600 : null); // seconds (10 min for demo)
  const [voiceLang, setVoiceLang] = useState("en-US");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [avatarVideoUrl, setAvatarVideoUrl] = useState("");
  const [question, setQuestion] = useState(isDemo
    ? "I was born July 12, 1994. Which gemstone aligns with my Cancer nature and would also be a sound long-term investment — Burmese ruby or Kashmir sapphire?"
    : "");
  const [answer, setAnswer] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [speaking, setSpeaking] = useState(false);

  // Load the master avatar (re-fetch when locale changes so we serve the
  // culturally distinct Sora video for the active language).
  useEffect(() => {
    api.get("/gemologist/avatar", { params: { lang: apiLang || "en" } }).then(({ data }) => {
      if (data?.data_url) setAvatarUrl(data.data_url);
      if (data?.video_url) {
        const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
        setAvatarVideoUrl(`${base}${data.video_url}`);
      } else {
        setAvatarVideoUrl("");
      }
    }).catch(() => {});
  }, [apiLang]);

  // Poll Stripe status until paid (or timeout). Skipped in demo mode.
  useEffect(() => {
    if (isDemo) return;
    if (!consultationId) {
      setStatusState("failed");
      return;
    }
    let cancelled = false;
    let elapsed = 0;
    const tick = async () => {
      try {
        const { data } = await api.get(`/consultation/status/${consultationId}`);
        if (cancelled) return;
        if (data.voice_lang) setVoiceLang(data.voice_lang);
        if (data.payment_status === "paid") {
          setRemaining(data.remaining_seconds);
          if (data.answer) {
            setAnswer(data.answer);
            setStatusState("answered");
            // Don't auto-speak on resume — user may have refreshed; show a Play button instead.
          } else {
            setStatusState("paid");
          }
          // Clean up the localStorage marker once we know payment succeeded
          if (typeof window !== "undefined") localStorage.removeItem("opik_consult_pending");
          return; // stop polling
        }
        elapsed += PAYMENT_POLL_INTERVAL_MS;
        if (elapsed >= PAYMENT_POLL_TIMEOUT_MS) {
          setStatusState("failed");
          return;
        }
        setTimeout(tick, PAYMENT_POLL_INTERVAL_MS);
      } catch {
        if (!cancelled) setStatusState("failed");
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [consultationId, isDemo]);

  // Countdown timer (display only — server enforces actual expiry)
  useEffect(() => {
    if (statusState !== "paid" || remaining === null) return;
    const id = setInterval(() => setRemaining((r) => (r > 0 ? r - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [statusState, remaining]);

  // Cleanup speech on unmount
  useEffect(() => () => {
    if (typeof window !== "undefined" && window.speechSynthesis) window.speechSynthesis.cancel();
  }, []);

  const speak = (text, vlang = voiceLang) => {
    if (!text || typeof window === "undefined" || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = vlang;
    u.rate = 0.95;
    const voices = window.speechSynthesis.getVoices();
    const match = voices.find((v) => v.lang === vlang)
      || voices.find((v) => v.lang?.startsWith(vlang.slice(0, 2)));
    if (match) u.voice = match;
    u.onstart = () => { setSpeaking(true); window.__opikSpeechWord = 0; };
    u.onend = () => { setSpeaking(false); window.__opikSpeechWord = -1; };
    u.onerror = () => { setSpeaking(false); window.__opikSpeechWord = -1; };
    u.onboundary = (e) => {
      if (e.name === "word") {
        const idx = (window.__opikSpeechWord ?? 0) + 1;
        window.__opikSpeechWord = idx;
        const upcoming = (text.slice(e.charIndex, e.charIndex + (e.charLength || 1)) || "").toLowerCase();
        window.__opikSpeechViseme = pickViseme(upcoming);
      }
    };
    window.speechSynthesis.speak(u);
  };

  const stopSpeak = () => {
    if (typeof window !== "undefined" && window.speechSynthesis) window.speechSynthesis.cancel();
    setSpeaking(false);
  };

  const submitQuestion = async () => {
    const q = question.trim();
    if (q.length < 5) { toast.error("Please describe your question in more detail."); return; }
    const maxLen = isDemo ? 250 : 1000;
    if (q.length > maxLen) { toast.error(`Please keep your question under ${maxLen} characters.`); return; }
    setSubmitting(true);
    try {
      const endpoint = isDemo ? "/consultation/demo" : "/consultation/ask";
      const body = isDemo
        ? { question: q, name: "Aria", birth_date: "1994-07-12", lang: voiceLang.split("-")[0] }
        : { consultation_id: consultationId, question: q };
      const { data } = await api.post(endpoint, body);
      setAnswer(data.answer);
      setStatusState("answered");
      setTimeout(() => speak(data.answer, data.voice_lang || voiceLang), 100);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not generate consultation.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-noir min-h-screen pt-28">
      <Seo
        title="Professional Gemologist Consultation"
        description="One-on-one paid reading from the Senior Gemologist of OPIKJEWELS."
        path="/consultation/success"
        noIndex
      />
      <section className="max-w-5xl mx-auto px-6 md:px-12 lg:px-16 py-12">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-1.5 bg-gold/15 border border-gold/40 text-gold font-accent text-[10px] tracking-[0.28em] uppercase px-3 py-1.5 mb-5">
            <ShieldCheck size={11} /> Professional Gemologist Consultation
          </div>
          <h1 className="font-heading text-4xl md:text-5xl text-ivory mb-2">Your Private Reading</h1>
          <p className="text-white/50 text-sm">Senior Gemologist · OPIKJEWELS Maison</p>
          {isDemo && (
            <div data-testid="consult-demo-banner" className="inline-flex items-center gap-2 mt-5 bg-blue-500/10 border border-blue-400/40 text-blue-200 font-accent text-[10px] tracking-[0.22em] uppercase px-3 py-1.5">
              ◷ Preview / Demo Mode · No charge
            </div>
          )}
        </div>

        {statusState === "verifying" && (
          <div data-testid="consult-status-verifying" className="flex flex-col items-center py-20 text-white/60">
            <Loader2 size={28} className="animate-spin text-gold mb-4" />
            <div className="font-accent text-[11px] tracking-[0.24em] uppercase">Verifying your payment…</div>
          </div>
        )}

        {statusState === "failed" && (
          <div data-testid="consult-status-failed" className="border border-red-500/30 bg-red-500/5 p-8 text-center">
            <h3 className="font-heading text-2xl text-ivory mb-3">Consultation not found</h3>
            <p className="text-white/60 text-sm mb-6">
              We couldn't verify your payment. If you completed checkout, please refresh in a moment — Stripe
              webhooks can take up to a minute to settle. Otherwise return to the Gemologist and try again.
            </p>
            <Link
              to={`/${slug}/find-your-stone`}
              className="inline-flex items-center gap-2 border border-gold text-gold font-accent text-[10px] tracking-[0.24em] uppercase px-5 py-3 hover:bg-gold hover:text-black transition-colors"
            >
              Return to Gemologist <ArrowRight size={12} />
            </Link>
          </div>
        )}

        {(statusState === "paid" || statusState === "answered") && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-start">
            <div className="bg-onyx border border-white/10 p-6">
              <div className="relative aspect-[4/5] bg-black overflow-hidden border border-white/10" data-testid="consult-avatar">
                {avatarUrl ? (
                  <>
                    {/* Show the still ONLY if no Sora video — avoids face-mismatch */}
                    {!avatarVideoUrl && (
                      <img
                        src={avatarUrl}
                        alt="Senior Gemologist of Opik Jewels"
                        className="w-full h-full object-cover"
                        style={{
                          filter: "saturate(1.05) contrast(1.05)",
                          animation: speaking ? "none" : "consult-breath 5s ease-in-out infinite",
                        }}
                      />
                    )}
                    {/* Sora-2 talking-head loop (when available) */}
                    <TalkingHeadVideo videoUrl={avatarVideoUrl} speaking={speaking} />
                    {/* Fallback viseme canvas overlay when no video is generated yet */}
                    {!avatarVideoUrl && <MouthCanvas speaking={speaking} />}
                  </>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-white/40">
                    <Loader2 size={20} className="animate-spin" />
                  </div>
                )}
                {speaking && (
                  <div className="absolute bottom-4 right-4 flex items-center gap-2 bg-black/70 border border-gold/40 px-3 py-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
                    <span className="font-accent text-[9px] tracking-[0.24em] uppercase text-gold">Speaking</span>
                  </div>
                )}
                <style>{`
                  @keyframes consult-breath {
                    0%, 100% { transform: scale(1); }
                    50%      { transform: scale(1.008); }
                  }
                `}</style>
              </div>
              {remaining !== null && (
                <div className="mt-4 flex items-center gap-2 text-white/50 text-xs">
                  <Clock size={12} />
                  <span data-testid="consult-timer">Access window: {fmtMMSS(remaining)} remaining</span>
                </div>
              )}
            </div>

            <div>
              {statusState === "paid" && (
                <>
                  <div className="font-accent text-[10px] tracking-[0.24em] uppercase text-gold mb-3">
                    Your question — one detailed reading
                  </div>
                  <textarea
                    data-testid="consult-question"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    rows={8}
                    placeholder="e.g. I was born July 12, 1994. Which gemstone aligns with my Cancer nature and would also be a sound long-term investment for my collection? Should I buy a Burmese ruby or a Kashmir sapphire next?"
                    className="w-full bg-transparent border border-white/20 p-4 text-white text-sm leading-relaxed focus:outline-none focus:border-gold resize-none"
                    maxLength={isDemo ? 250 : 1000}
                  />
                  <div className="text-right text-white/30 text-[10px] mb-3">{question.length} / {isDemo ? 250 : 1000}</div>
                  <button
                    data-testid="consult-ask"
                    onClick={submitQuestion}
                    disabled={submitting || question.trim().length < 5}
                    className="w-full flex items-center justify-center gap-2 bg-gold text-black font-accent text-[11px] tracking-[0.28em] uppercase py-4 hover:bg-ivory transition-colors duration-500 disabled:opacity-50"
                  >
                    {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={13} />}
                    {submitting ? "Composing your reading…" : "Receive My Reading"}
                  </button>
                </>
              )}

              {statusState === "answered" && answer && (
                <>
                  <div className="font-accent text-[10px] tracking-[0.24em] uppercase text-gold mb-3">
                    Your Reading
                  </div>
                  <div
                    data-testid="consult-answer"
                    className="text-white/85 text-base leading-relaxed font-light italic border-l-2 border-gold/40 pl-5 mb-5"
                  >
                    "{answer}"
                  </div>
                  <div className="flex items-center gap-3">
                    {speaking ? (
                      <button
                        data-testid="consult-pause"
                        onClick={stopSpeak}
                        className="flex items-center gap-2 border border-gold text-gold font-accent text-[10px] tracking-[0.24em] uppercase px-4 py-2.5 hover:bg-gold hover:text-black transition-colors"
                      >
                        <Pause size={12} /> Stop
                      </button>
                    ) : (
                      <button
                        data-testid="consult-replay"
                        onClick={() => speak(answer)}
                        className="flex items-center gap-2 border border-gold text-gold font-accent text-[10px] tracking-[0.24em] uppercase px-4 py-2.5 hover:bg-gold hover:text-black transition-colors"
                      >
                        <Play size={12} /> Play / Replay
                      </button>
                    )}
                    <span className="font-accent text-[10px] tracking-[0.22em] uppercase text-white/40">{voiceLang}</span>
                  </div>

                  <div className="mt-8 pt-6 border-t border-white/10 text-white/50 text-xs">
                    Reading saved to <span className="text-white/80">your consultation history</span>. View past readings any time at{" "}
                    <Link to={`/${slug}/consultations`} className="text-gold hover:text-ivory underline">
                      /consultations
                    </Link>.
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

const fmtMMSS = (s) => {
  if (s === null || s < 0) return "—";
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
};

export default ConsultationSuccess;
